<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import IndicatorConfigPicker from '@/components/Management/Indicators/IndicatorConfigPicker.vue'
import type { IAreaSensor, IManagementArea } from '@/interfaces/entities/management'
import { indicatorsForType } from '@/interfaces/entities/management'
import type { ISensor } from '@/interfaces/entities/sensor'
import { getSensorColor, SensorType, translateSensorType } from '@/interfaces/enums'
import { matchesSearchAccentInsensitive } from '@/utils/string/search-normalize'

const props = defineProps<{
  isOpen: boolean
  area: IManagementArea | null
  sensors: ISensor[]
  editing?: IAreaSensor | null
}>()

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'save', payload: { sensorId: number; indicators: string[] }): void
  (e: 'save-changes', payload: { add: { sensorId: number; indicators: string[] }[]; remove: number[] }): void
}>()

const isEditing = computed(() => !!props.editing)

// Apenas tipos com indicadores configuráveis (exclui rotação).
const TYPE_OPTIONS = [SensorType.ELECTRICITY, SensorType.TEMPERATURE, SensorType.DOOR_OPEN]

const typeFilter = ref<'all' | SensorType>('all')
const search = ref('')
const selectedIds = ref<Set<number>>(new Set())
const indicators = ref<string[]>([])

// Sensores já vinculados à área (sensor_id -> area_sensor) para pré-marcar e remover.
const existingBySensorId = computed(() => {
  const map = new Map<number, IAreaSensor>()
  ;(props.area?.management_area_sensors || []).forEach((areaSensor) => map.set(areaSensor.sensor_id, areaSensor))
  return map
})

// Todos os sensores com indicadores configuráveis (inclui os que já estão na área).
const manageableSensors = computed(() =>
  props.sensors.filter((sensor) => indicatorsForType(String(sensor.sensor_type)).length > 0),
)

const visibleSensors = computed(() =>
  manageableSensors.value.filter(
    (sensor) =>
      (typeFilter.value === 'all' || String(sensor.sensor_type) === typeFilter.value) &&
      (search.value === '' || matchesSearchAccentInsensitive(sensor.name, search.value)),
  ),
)

const allVisibleSelected = computed(
  () => visibleSensors.value.length > 0 && visibleSensors.value.every((s) => selectedIds.value.has(s.id)),
)

const showSingleTypePicker = computed(() => typeFilter.value !== 'all')

// Diferenças em relação ao estado atual da área.
const addedIds = computed(() => [...selectedIds.value].filter((id) => !existingBySensorId.value.has(id)))
const removedAreaSensors = computed(() =>
  [...existingBySensorId.value.values()].filter((areaSensor) => !selectedIds.value.has(areaSensor.sensor_id)),
)
const hasChanges = computed(() => addedIds.value.length > 0 || removedAreaSensors.value.length > 0)

const canSave = computed(() => {
  if (isEditing.value) return indicators.value.length > 0
  return hasChanges.value
})

function resetState() {
  typeFilter.value = 'all'
  search.value = ''
  // Pré-marca os sensores que já estão na área.
  selectedIds.value = new Set(existingBySensorId.value.keys())
  indicators.value = []
}

watch(
  () => props.isOpen,
  (open) => {
    if (!open) return
    if (props.editing) {
      indicators.value = [...props.editing.indicators]
    } else {
      resetState()
    }
  },
)

// Ao filtrar por um tipo específico, pré-seleciona todos os indicadores daquele tipo (para novos).
watch(typeFilter, (type) => {
  indicators.value = type === 'all' ? [] : indicatorsForType(type).map((indicator) => indicator.key)
})

function toggle(id: number) {
  const next = new Set(selectedIds.value)
  next.has(id) ? next.delete(id) : next.add(id)
  selectedIds.value = next
}

function toggleAllVisible() {
  const next = new Set(selectedIds.value)
  if (allVisibleSelected.value) {
    visibleSensors.value.forEach((s) => next.delete(s.id))
  } else {
    visibleSensors.value.forEach((s) => next.add(s.id))
  }
  selectedIds.value = next
}

function submit() {
  if (!canSave.value) return

  if (isEditing.value && props.editing) {
    emit('save', { sensorId: props.editing.sensor_id, indicators: indicators.value })
    return
  }

  // Novos sensores: indicadores SEMPRE pelo tipo do próprio sensor.
  const add = addedIds.value
    .map((id) => {
      const sensor = manageableSensors.value.find((s) => s.id === id)
      if (!sensor) return null
      const type = String(sensor.sensor_type)
      const allForType = indicatorsForType(type).map((i) => i.key)
      const inds =
        typeFilter.value !== 'all' && type === typeFilter.value
          ? indicators.value.filter((key) => allForType.includes(key))
          : allForType
      return inds.length ? { sensorId: id, indicators: inds } : null
    })
    .filter((item): item is { sensorId: number; indicators: string[] } => item !== null)

  const remove = removedAreaSensors.value.map((areaSensor) => areaSensor.id)

  if (add.length || remove.length) emit('save-changes', { add, remove })
}
</script>

<template>
  <div v-if="isOpen" class="fixed inset-0 z-[60] flex items-center justify-center p-4">
    <div class="absolute inset-0 bg-black/40" @click="emit('cancel')" />
    <div class="relative flex max-h-[90vh] w-full max-w-3xl flex-col bg-white p-6 shadow-xl">
      <h3 class="text-lg font-semibold text-slate-900">
        {{ isEditing ? 'Editar indicadores do sensor' : 'Sensores da área' }}
      </h3>
      <p v-if="area" class="mt-1 text-sm text-slate-500">
        Área: <strong>{{ area.name }}</strong>
        <template v-if="!isEditing"> — marque para adicionar, desmarque para remover.</template>
      </p>

      <!-- EDIÇÃO: um único sensor -->
      <div v-if="isEditing" class="mt-4 space-y-5 overflow-y-auto">
        <div class="flex items-center gap-2 border border-stroke px-3 py-2 text-sm">
          <span class="text-slate-700">{{ editing?.sensor_name }}</span>
          <span class="ml-auto text-xs text-slate-400">{{ translateSensorType(editing?.sensor_type as SensorType) }}</span>
        </div>
        <div>
          <p class="mb-2 text-sm font-medium text-slate-700">Indicadores a exibir</p>
          <IndicatorConfigPicker :sensor-type="editing?.sensor_type" v-model="indicators" />
        </div>
      </div>

      <!-- GERENCIAR (adicionar / remover) -->
      <div v-else class="mt-4 flex min-h-0 flex-1 flex-col gap-4">
        <!-- Filtros -->
        <div class="flex flex-wrap items-center gap-2">
          <div class="flex border border-stroke">
            <button
              type="button"
              class="px-3 py-1.5 text-sm font-medium"
              :class="typeFilter === 'all' ? 'bg-primary text-white' : 'text-slate-600'"
              @click="typeFilter = 'all'">
              Todos
            </button>
            <button
              v-for="type in TYPE_OPTIONS"
              :key="type"
              type="button"
              class="border-l border-stroke px-3 py-1.5 text-sm font-medium"
              :class="typeFilter === type ? 'bg-primary text-white' : 'text-slate-600'"
              @click="typeFilter = type">
              {{ translateSensorType(type) }}
            </button>
          </div>
          <input
            v-model="search"
            type="text"
            placeholder="Buscar sensor..."
            class="flex-1 border border-zinc-300 px-3 py-1.5 text-sm focus:border-primary focus:outline-none" />
        </div>

        <!-- Lista de sensores com checkbox -->
        <div class="min-h-0 flex-1 overflow-y-auto border border-stroke">
          <div class="flex items-center justify-between border-b border-stroke bg-slate-50 px-3 py-2 text-xs">
            <label class="flex cursor-pointer items-center gap-2 text-slate-600">
              <input type="checkbox" class="h-4 w-4 accent-primary" :checked="allVisibleSelected" @change="toggleAllVisible" />
              Selecionar todos ({{ visibleSensors.length }})
            </label>
            <span class="text-slate-500">{{ selectedIds.size }} na área</span>
          </div>
          <label
            v-for="sensor in visibleSensors"
            :key="sensor.id"
            class="flex cursor-pointer items-center gap-3 border-b border-stroke px-3 py-2 text-sm hover:bg-slate-50">
            <input
              type="checkbox"
              class="h-4 w-4 accent-primary"
              :checked="selectedIds.has(sensor.id)"
              @change="toggle(sensor.id)" />
            <span class="min-w-0 flex-1 truncate text-slate-700">{{ sensor.name }}</span>
            <span v-if="existingBySensorId.has(sensor.id)" class="shrink-0 text-[10px] font-medium text-emerald-600">
              na área
            </span>
            <span class="shrink-0 px-1.5 py-0.5 text-[10px] font-medium text-white" :class="getSensorColor(sensor.sensor_type)">
              {{ translateSensorType(sensor.sensor_type) }}
            </span>
          </label>
          <p v-if="!visibleSensors.length" class="px-3 py-8 text-center text-sm text-slate-400">
            Nenhum sensor disponível para este filtro.
          </p>
        </div>

        <!-- Indicadores para os novos -->
        <div v-if="showSingleTypePicker">
          <p class="mb-2 text-sm font-medium text-slate-700">
            Indicadores dos novos sensores
            <span class="text-slate-400">(de {{ translateSensorType(typeFilter as SensorType) }})</span>
          </p>
          <IndicatorConfigPicker :sensor-type="typeFilter" v-model="indicators" />
        </div>
        <p v-else class="text-xs text-slate-500">
          Sensores adicionados recebem os indicadores padrão do seu tipo (ajuste por sensor depois com o ícone ✎).
          Para escolher indicadores específicos ao adicionar, filtre por um tipo.
        </p>
      </div>

      <div class="mt-4 flex items-center justify-between gap-3 border-t border-stroke pt-4">
        <p v-if="!isEditing && hasChanges" class="text-xs text-slate-500">
          <span v-if="addedIds.length" class="text-emerald-600">+{{ addedIds.length }} adicionar</span>
          <span v-if="addedIds.length && removedAreaSensors.length"> · </span>
          <span v-if="removedAreaSensors.length" class="text-red">−{{ removedAreaSensors.length }} remover</span>
        </p>
        <span v-else />
        <div class="flex gap-3">
          <button
            type="button"
            class="border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            @click="emit('cancel')">
            Cancelar
          </button>
          <button
            type="button"
            :disabled="!canSave"
            class="bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            @click="submit">
            {{ isEditing ? 'Salvar' : 'Salvar alterações' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
